import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { GLOSSARY } from "~/generated/glossary.data";
import type { GlossaryId } from "~/generated/glossary.types";
import GlossaryTooltip from "~/widgets/GlossaryTooltip/GlossaryTooltip";
import styles from "~/widgets/GlossaryInfoIcon/GlossaryInfoIcon.module.scss";

export interface GlossaryInfoIconProps {
  /** The glossary entry ID to display */
  glossaryId: GlossaryId;
  /** Optional custom class name for styling */
  className?: string;
}

interface TooltipState {
  show: boolean;
  position: { top: number; left: number };
}

/**
 * GlossaryInfoIcon Component
 *
 * A reusable info icon (ⓘ) that displays a glossary tooltip on hover/click.
 * Use this whenever you need to show glossary information for a term.
 *
 * Features:
 * - Shows tooltip on hover and click
 * - Positions tooltip below the icon
 * - Uses portal to render tooltip at document body level
 */
const GlossaryInfoIcon: React.FC<GlossaryInfoIconProps> = ({
  glossaryId,
  className,
}) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    show: false,
    position: { top: 0, left: 0 },
  });
  const iconRef = useRef<HTMLSpanElement>(null);
  const entry = GLOSSARY[glossaryId];

  const showTooltip = () => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    setTooltip({
      show: true,
      position: {
        top: rect.bottom + 8,
        left: rect.left,
      },
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, position: { top: 0, left: 0 } });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    showTooltip();
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    showTooltip();
  };

  if (!entry) return null;

  return (
    <>
      <span
        ref={iconRef}
        className={`${styles.infoIcon} ${className || ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={hideTooltip}
        onClick={handleClick}
        title="Hover for details"
        role="button"
        tabIndex={-1}
        aria-label={`Information about ${glossaryId}`}
      >
        ⓘ
      </span>

      {tooltip.show && createPortal(
        <GlossaryTooltip
          entry={entry}
          position={tooltip.position}
          onClose={hideTooltip}
        />,
        document.body
      )}
    </>
  );
};

export default GlossaryInfoIcon;
